import React from 'react';
import { Grid2 as Grid, Stack, styled, Typography } from '@mui/material';
import { FormattedTextField, LinkField } from 'framework/ui/field';
import IssuesStaffingDashboardField from '../shared/fields/IssuesStaffingDashboardField';
import { FollowUpField } from 'domain/shared/core/dashboard';
import StaffingDashboardAssignCoordinatorField from '../shared/fields/StaffingDashboardAssignCoordinatorField';
import StaffingDashboardZoneChangeButton from '../shared/fields/StaffingDashboardZoneChangeButton';
import { calculateEnhancedTeamStructure } from 'domain/shared/newProject';
import { useShowContext } from 'framework/show';
import { StaffingDataType } from 'domain/shared/staffing';
import { EMPTY_VALUE_INDICATOR, formatDecimal } from 'framework/utils';
import { AppIconLabel } from 'framework/ui/mui-builder';
import { IconLabel } from 'domain/staffing/dashboard/opportunityDashboard/List/OpportunityDashboardGeneralInfo';

export const StyledGrid = styled(Grid)(() => ({
  fontSize: '12px',
  '.MuiTypography-root': {
    fontSize: '12px',
  },
  '.field__wrapper > .MuiTypography-root': {
    fontSize: '14px',
  },
}));

interface StaffingProjectItemCollapsedProps {
  projectPath: string;
  projectName: string;
}

const StaffingProjectItemCollapsed: React.FC<StaffingProjectItemCollapsedProps> = ({ projectPath, projectName }) => {
  const { data } = useShowContext<StaffingDataType>();
  const { team, ...projectData } = data.getData() || {};
  const { allFte = 0, assignedFte = 0 } = calculateEnhancedTeamStructure(
    team,
    projectData?.budget?.fte,
    projectData?.budget?.billingType,
  );

  const isAvailable = allFte != null && allFte > 0;
  const percentage = allFte > 0 ? Math.round((assignedFte / allFte) * 100) : assignedFte > 0 ? 100 : 0;

  return (
    <Grid
      container
      className="dashboard-item-content"
      columns={12}
      spacing={2}
      justifyContent="space-between"
      alignItems="center"
    >
      <StyledGrid size={2} display="flex">
        <LinkField value={projectPath} linkText={projectName} label="" />
      </StyledGrid>

      <StyledGrid size={{ xs: 2 }} display="flex" flexDirection="row">
        <FormattedTextField
          value={
            isAvailable
              ? `${formatDecimal(assignedFte)}/${formatDecimal(allFte)} FTE (${formatDecimal(percentage)}%)`
              : EMPTY_VALUE_INDICATOR
          }
          format={(str: string) => {
            return (
              <Stack direction="row" alignItems="center">
                <IconLabel>
                  <AppIconLabel icon="group" tooltipTitle="Assigned/Required FTE" />
                </IconLabel>
                <Typography>{str}</Typography>
              </Stack>
            );
          }}
        />
      </StyledGrid>
      <StyledGrid size={{ xs: 1.5 }}>
        <FollowUpField source="staffingRisk.followUps" />
      </StyledGrid>

      <StyledGrid size={{ xs: 3.5 }}>
        <IssuesStaffingDashboardField />
      </StyledGrid>

      <StyledGrid size={{ xs: 2 }} sx={{ pl: 2 }}>
        <StaffingDashboardAssignCoordinatorField />
      </StyledGrid>

      <StyledGrid size={{ xs: 1 }}>
        <StaffingDashboardZoneChangeButton
          buttonSx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
        />
      </StyledGrid>
    </Grid>
  );
};

export default StaffingProjectItemCollapsed;
